import * as React from 'react';
import { ComplexValue, DrawerWithBar } from 'ui';
import { DragNDrop } from '../components/drag-n-drop';
import styles from '../selection-report.scss';
import { ReportSelectedOptions } from '../components/selected-options';
import { AggregationState } from 'app/area/reports/containers/aggregation/types-store';
import { GroupByFieldState } from 'app/area/reports/containers/group-by/types-store';
import { TargetState } from 'app/area/reports/containers/target/types-store';
import { ChartState } from 'app/area/reports/containers/chart/types-store';
import { AggregationContainerProps } from 'app/area/reports/containers/aggregation/aggregation-container';
import { GroupByContainerProps } from 'app/area/reports/containers/group-by/group-by-container';
import { TargetContainerProps } from 'app/area/reports/containers/target/target-container';
import { ChartContainerProps } from 'app/area/reports/containers/chart/chart-container';
import { SelectionFieldItem } from 'app/area/reports/containers/group-by/types-fields';
import { DistributionContainerProps } from 'app/area/reports/containers/distribution/distribution-container';
import { DistributionState } from 'app/area/reports/containers/distribution/types-store';
import { DrillDownCallback } from 'app/components/favourite-layout/favourite/common/drill-down-model';

interface FrequencyReportViewProps {
  favouriteId: number;
  aggregateFieldState: AggregationState;
  groupByFieldState: GroupByFieldState;
  targetFieldState: TargetState;
  chartState: ChartState;
  distributionFieldState: DistributionState;
  translation: any;
  initiallyOpen: boolean;
  AggregationContainer: React.ComponentType<AggregationContainerProps>;
  GroupByContainer: React.ComponentType<GroupByContainerProps>;
  TargetContainer: React.ComponentType<TargetContainerProps>;
  ChartContainer: React.ComponentType<ChartContainerProps>;
  DistributionContainer: React.ComponentType<DistributionContainerProps>;
  hideVisualizationOptions: boolean;
  disabledTarget: boolean;
  selectedAggregationField: ComplexValue<number>;
  selectedAggregationFunction: ComplexValue<number>;
  selectedUomSet: ComplexValue<number>;
  selectedTargetApplication: ComplexValue<number>;
  selectedGroupByFields: SelectionFieldItem[];
  selectedExposureApplication: ComplexValue<number>;
  selectedExposureFactor?: number;
  selectedExposurePercent?: number;
  requestCustomWidth?: (id: number, val: any) => void;
  drillDown?: DrillDownCallback;
  reflowHandle?: string;
}

export const FrequencyReportView = ({
  favouriteId,
  aggregateFieldState,
  distributionFieldState,
  groupByFieldState,
  targetFieldState,
  chartState,
  translation,
  initiallyOpen,
  AggregationContainer,
  DistributionContainer,
  GroupByContainer,
  TargetContainer,
  ChartContainer,
  hideVisualizationOptions,
  selectedAggregationField,
  selectedAggregationFunction,
  selectedUomSet,
  selectedTargetApplication,
  selectedGroupByFields,
  selectedExposureApplication,
  selectedExposureFactor,
  selectedExposurePercent,
  requestCustomWidth,
  drillDown,
  reflowHandle
}: FrequencyReportViewProps) => {

  const chartContainer = <ChartContainer 
    chartState={chartState}
    favouriteId={favouriteId}
    translation={translation}
    requestCustomWidth={requestCustomWidth}
    drillDown={drillDown}
    reflowHandle={reflowHandle}
  />;

  if (hideVisualizationOptions) {
    return chartContainer;
  }

  const disabledGroupBy = !selectedExposureApplication?.value || !selectedAggregationField?.value;

  const disabledTarget = !selectedAggregationField?.value;

  const barContent = <ReportSelectedOptions
    selectedAggregationField={selectedAggregationField}
    selectedAggregationFunction={selectedAggregationFunction}
    selectedUomSet={selectedUomSet}
    selectedTargetApplication={selectedTargetApplication}
    selectedGroupByFields={selectedGroupByFields}
    selectedExposureApplication={selectedExposureApplication}
    selectedExposureFactor={selectedExposureFactor}
    selectedExposurePercent={selectedExposurePercent}
    translation={translation}
  />;

  const panels = (
    <DragNDrop>
      <AggregationContainer 
        orderNumber="1"
        aggregateFieldState={aggregateFieldState}
        translation={translation}
        fieldsToClearOnChange={{target: true, rollingAverage: true, selections: true}}
      />
      <DistributionContainer 
        orderNumber="2"
        distributionFieldState={distributionFieldState}
        translation={translation}
      />
      <GroupByContainer 
        orderNumber="3"
        disabled={disabledGroupBy}
        groupByFieldState={groupByFieldState}
        chartState={chartState}
        translation={translation}
        isFrequencyReport={true}
      />
      <TargetContainer 
        orderNumber="4"
        disabled={disabledTarget}
        targetFieldState={targetFieldState}
        translation={translation}
        selectedTargetApplication={selectedTargetApplication}
      />
    </DragNDrop>
  );

  return (
    <div className={styles.selectionReportView}>
      <DrawerWithBar
        title={translation.selection_report_extend_btn}
        barContent={barContent}
        drawerContent={panels}
        initiallyOpen={initiallyOpen}
      >
        {chartContainer}
      </DrawerWithBar>
    </div>
  );
};
